import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { cartQuantityQueryOptions, cartKeys } from '@lib/query/options/cart';

// =============================================================================
// Types
// =============================================================================

export interface CartDrawerContextValue {
  /** Whether the cart drawer is open */
  isOpen: boolean;
  /** Toggle the cart drawer open/closed */
  toggleDrawer: () => void;
  /** Open the cart drawer */
  openDrawer: () => void;
  /** Close the cart drawer */
  closeDrawer: () => void;
  /** Total quantity of items in cart */
  cartQuantity: number;
  /** Whether cart quantity is loading */
  isLoading: boolean;
  /** Whether cart quantity fetch failed */
  isError: boolean;
  /** Invalidate cart queries to trigger refetch */
  invalidateCart: () => Promise<void>;
  /** Whether user is logged in - cart operations require login */
  isLoggedIn: boolean;
}

// =============================================================================
// Context
// =============================================================================

const CartDrawerContext = createContext<CartDrawerContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface CartDrawerProviderProps {
  children: ReactNode;
  /** Whether user is logged in - cart queries are skipped when false */
  isLoggedIn: boolean;
}

/**
 * Cart Drawer Provider
 *
 * Manages:
 * - Cart drawer open/close state
 * - Cart quantity for badge display
 * - Cart query invalidation
 *
 * Note: Cart queries are only executed when user is logged in.
 * This is a business requirement - anonymous users don't have carts.
 */
export function CartDrawerProvider({
  children,
  isLoggedIn,
}: CartDrawerProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();

  // Fetch cart quantity for badge - only when logged in
  const {
    data: cartQuantity = 0,
    isLoading,
    isError,
  } = useQuery({
    ...cartQuantityQueryOptions(),
    enabled: isLoggedIn,
  });

  const toggleDrawer = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openDrawer = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
  }, []);

  const invalidateCart = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: cartKeys.all });
  }, [queryClient]);

  return (
    <CartDrawerContext.Provider
      value={{
        isOpen,
        toggleDrawer,
        openDrawer,
        closeDrawer,
        cartQuantity,
        isLoading,
        isError,
        invalidateCart,
        isLoggedIn,
      }}
    >
      {children}
    </CartDrawerContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

/**
 * Hook to access cart drawer context.
 * Must be used within CartDrawerProvider.
 */
export function useCartDrawer() {
  const context = useContext(CartDrawerContext);
  if (!context) {
    throw new Error('useCartDrawer must be used within CartDrawerProvider');
  }
  return context;
}
